import { Flex, Burger, Avatar, Drawer } from '@mantine/core';
import { useThemedLayoutContext } from '@refinedev/mantine';
import { Site, Organization } from 'interfaces';
import { AdminSiteSelector } from './AdminSiteSelector';
import { ColorSchemeToggle } from './ColorSchemeToggle';
import { ProviderSiteSelector } from './ProviderSiteSelector';
import { SiteSelector } from './SiteSelector';
import { Title as Logo } from 'components/layout/title';
import { useCurrentUserStore } from 'store/UserStore';
import { useMediaQuery } from '@mantine/hooks';
import { Sider } from '../sider';
import { NotificationBell } from 'components/notifications/NotificationBell';
import { ChatToolbarButton } from 'components/chat/ChatManager';

interface MobileHeaderProps {
  selectedSiteID: string;
  setSelectedSiteID: React.Dispatch<React.SetStateAction<string>>;
  handleSiteChange: (value: string | null) => void;
  fetchedSites: boolean;
  setFetchedSites: React.Dispatch<React.SetStateAction<boolean>>;
  setSites: React.Dispatch<React.SetStateAction<Site[]>>;
  setOrganizations: React.Dispatch<React.SetStateAction<Organization[]>>;
  setFetchedOrganizations: React.Dispatch<React.SetStateAction<boolean>>;
  isAdmin: boolean;
  isProvider: boolean;
  computedColorScheme: 'light' | 'dark';
  toggleColorScheme: () => void;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  isAdmin,
  isProvider,
  selectedSiteID,
  handleSiteChange,
  setFetchedSites,
  setFetchedOrganizations,
  setSites,
  setOrganizations,
  computedColorScheme,
  toggleColorScheme,
}) => {
  const showLogo = useMediaQuery('(min-width: 500px)');
  const { firstName, lastName } = useCurrentUserStore();
  const { mobileSiderOpen, setMobileSiderOpen } = useThemedLayoutContext();

  return (
    <>
      <Flex justify={'space-between'} align="center" gap="md" pr={'md'}>
        {showLogo && <Logo size={50} collapsed={mobileSiderOpen} />}
        <Burger
          opened={mobileSiderOpen}
          onClick={() => setMobileSiderOpen(!mobileSiderOpen)}
          aria-label="Open navigation menu"
        />
      </Flex>
      <Flex align="center" gap="md" justify={'flex-end'} w="75%">
        {!isProvider && !isAdmin && (
          <SiteSelector
            selectedSiteID={selectedSiteID}
            handleSiteChange={handleSiteChange}
            setFetchedSites={setFetchedSites}
            setSites={setSites}
          />
        )}
        {isProvider && (
          <ProviderSiteSelector
            selectedSiteID={selectedSiteID}
            setOrganizations={setOrganizations}
            setSites={setSites}
            handleSiteChange={handleSiteChange}
            setFetchedOrganizations={setFetchedOrganizations}
          />
        )}
        {isAdmin && (
          <AdminSiteSelector
            isAdmin={isAdmin}
            setFetchOrganizations={setFetchedOrganizations}
            setOrganizations={setOrganizations}
            handleSiteChange={handleSiteChange}
            setSites={setSites}
            selectedSiteID={selectedSiteID}
          />
        )}
        <NotificationBell />
        <ChatToolbarButton />
        <Avatar radius="xl">
          {firstName?.[0]}
          {lastName?.[0]}
        </Avatar>
      </Flex>
      <Drawer
        opened={mobileSiderOpen}
        onClose={() => setMobileSiderOpen(false)}
        padding="md"
        size="80%"
        closeOnClickOutside
      >
        <ColorSchemeToggle
          colorScheme={computedColorScheme}
          toggleColorScheme={toggleColorScheme}
        />
        <Flex direction="column" gap="md" align="stretch">
          <Sider />
        </Flex>
      </Drawer>
    </>
  );
};
